<script setup>
import { computed } from 'vue';

const props = defineProps({
  icon: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  variant: {
    type: String,
    default: 'subtle',
  },
  size: {
    type: String,
    default: 'md',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  iconClass: {
    type: String,
    default: '',
  },
});

defineOptions({
  inheritAttrs: false,
});

const sizeClassMap = {
  xs: 'h-7 w-7',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
};

const iconSizeMap = {
  xs: 'size-4',
  sm: 'size-6',
  md: 'size-6',
};

const variantClassMap = {
  subtle:
    'border border-black/8 bg-white text-n-slate-11 shadow-sm hover:bg-n-slate-2',
  positive:
    'border border-[#25d366]/40 bg-[#128c7e] text-white shadow-sm hover:bg-[#0f6b5a]',
  positiveSoft:
    'bg-[#25d366]/12 text-[#128c7e] shadow-sm hover:bg-[#25d366]/18',
  danger:
    'bg-n-ruby-3 text-n-ruby-11 shadow-sm hover:bg-n-ruby-4 disabled:opacity-40',
  floatingDanger:
    'bg-white/96 text-n-ruby-11 shadow-[0_8px_20px_rgba(15,23,42,0.12)] ring-1 ring-black/5 hover:bg-n-ruby-2',
};

const sizeClass = computed(() => sizeClassMap[props.size] || sizeClassMap.md);
const resolvedIconClass = computed(
  () => props.iconClass || iconSizeMap[props.size] || iconSizeMap.md
);
const variantClass = computed(
  () => variantClassMap[props.variant] || variantClassMap.subtle
);
</script>

<template>
  <button
    type="button"
    class="flex flex-shrink-0 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-35"
    :class="[sizeClass, variantClass]"
    :title="title"
    :disabled="disabled"
    v-bind="$attrs"
  >
    <i :class="[icon, resolvedIconClass]" />
  </button>
</template>
